import { useState } from "react"
import type { ReactNode } from "react"
import { reportingProductSchema, reportingSourceSchema } from "@aix/aixinternal-shared/reporting"
import type { ReportingProduct } from "@aix/aixinternal-shared/reporting"
import { reportingOperationalBindingSchema } from "@aix/aixinternal-shared/reportingOperations"
import type { ReportingOperationalBinding } from "@aix/aixinternal-shared/reportingOperations"
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@aix/ui"

import { trpc } from "../../lib/trpc"
import { SeoInventorySetup } from "./SeoInventorySetup"
import { formatReportingValidationIssues } from "./reportingValidation"

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {children}
    </label>
  )
}

/** Human configuration only. Report/collector service tokens cannot call these mutations. */
export function ReportsSetupPanel({
  companyId,
  products,
  onSaved,
}: {
  companyId: string
  products: ReportingProduct[]
  onSaved: () => void
}) {
  const [message, setMessage] = useState("")
  const [productKey, setProductKey] = useState("")
  const [productName, setProductName] = useState("")
  const [hosts, setHosts] = useState("")
  const [includedFrom, setIncludedFrom] = useState("")
  const [sourceProduct, setSourceProduct] = useState("")
  const [sourceKey, setSourceKey] = useState("")
  const [property, setProperty] = useState("")
  const [bindingProduct, setBindingProduct] = useState("")
  const [bindingKey, setBindingKey] = useState("")
  const [kind, setKind] = useState<ReportingOperationalBinding["kind"]>("uptime")
  const [externalId, setExternalId] = useState("")
  const [environment, setEnvironment] = useState("production")
  const [bindingStart, setBindingStart] = useState("")
  const [interval, setInterval] = useState("")
  const [confirmed, setConfirmed] = useState(false)
  const saved = () => {
    setMessage("Configuration saved in AIX. This does not collect metrics or execute monitors.")
    setConfirmed(false)
    onSaved()
  }
  const failed = (error: { message: string }) => setMessage(error.message)
  const productMutation = trpc.reporting.configureProduct.useMutation({ onSuccess: saved, onError: failed })
  const sourceMutation = trpc.reporting.configureSource.useMutation({ onSuccess: saved, onError: failed })
  const bindingMutation = trpc.reporting.configureOperationalBinding.useMutation({ onSuccess: saved, onError: failed })
  const isCheck = kind === "uptime" || kind === "scenario"
  const environmentOptions =
    kind === "scenario"
      ? ["prod", "dev", "local"]
      : kind === "incident-project"
        ? ["unknown"]
        : ["production", "staging", "preview", "development", "unknown"]
  const productOptions = products.map((product) => (
    <SelectItem key={product.key} value={product.key}>
      {product.name}
    </SelectItem>
  ))
  return (
    <Card>
      <CardHeader>
        <CardTitle>Reporting setup</CardTitle>
        <CardDescription>
          Human super-admin access required. Explicit saves write the shared AIX database; no provider operations run.
          Source identities are immutable in this version.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <details>
          <summary className="cursor-pointer text-sm font-medium">Add a product or verified source mapping</summary>
          <div className="mt-4 space-y-6">
            <form
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault()
                const parsed = reportingProductSchema.safeParse({
                  key: productKey,
                  name: productName,
                  hosts: hosts
                    .split(",")
                    .map((host) => host.trim())
                    .filter(Boolean),
                  includedFrom,
                  active: true,
                })
                if (!parsed.success) {
                  setMessage(formatReportingValidationIssues(parsed.error.issues))
                  return
                }
                productMutation.mutate({ companyId, product: parsed.data })
              }}
            >
              <h3 className="font-medium">1. Product identity</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Stable product key">
                  <Input
                    value={productKey}
                    onChange={(event) => setProductKey(event.target.value)}
                    placeholder="my-product"
                    required
                  />
                </Field>
                <Field label="Display name">
                  <Input value={productName} onChange={(event) => setProductName(event.target.value)} required />
                </Field>
                <Field label="Canonical hosts, comma separated (no URLs)">
                  <Input
                    value={hosts}
                    onChange={(event) => setHosts(event.target.value)}
                    placeholder="example.com,www.example.com"
                    required
                  />
                </Field>
                <Field label="Report inclusion date">
                  <Input
                    type="date"
                    value={includedFrom}
                    onChange={(event) => setIncludedFrom(event.target.value)}
                    required
                  />
                </Field>
              </div>
              <Button type="submit" loading={productMutation.isLoading}>
                Save active product
              </Button>
            </form>
            <form
              className="space-y-3 border-t border-border pt-4"
              onSubmit={(event) => {
                event.preventDefault()
                const parsed = reportingSourceSchema.safeParse({
                  key: sourceKey,
                  productKey: sourceProduct,
                  provider: "gsc",
                  channel: "organic-search",
                  externalId: property,
                  environment: "production",
                  timezone: "America/Los_Angeles",
                  currency: null,
                  attributionWindow: null,
                })
                if (!parsed.success) {
                  setMessage(formatReportingValidationIssues(parsed.error.issues))
                  return
                }
                sourceMutation.mutate({ companyId, source: parsed.data })
              }}
            >
              <h3 className="font-medium">2. Search Console source</h3>
              <p className="text-xs text-muted-foreground">
                Copy the exact property from verified GSC access inventory. A public hostname does not prove property
                access. Paid and analytics connectors are not enabled here.
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Product">
                  <Select value={sourceProduct || undefined} onValueChange={setSourceProduct}>
                    <SelectTrigger aria-label="GSC product">
                      <SelectValue placeholder="Choose product" />
                    </SelectTrigger>
                    <SelectContent>{productOptions}</SelectContent>
                  </Select>
                </Field>
                <Field label="Stable source key">
                  <Input
                    value={sourceKey}
                    onChange={(event) => setSourceKey(event.target.value)}
                    placeholder="my-product-gsc"
                    required
                  />
                </Field>
                <Field label="Verified GSC property">
                  <Input
                    value={property}
                    onChange={(event) => setProperty(event.target.value)}
                    placeholder="sc-domain:example.com"
                    required
                  />
                </Field>
              </div>
              <Button type="submit" loading={sourceMutation.isLoading} disabled={!sourceProduct}>
                Save GSC binding
              </Button>
            </form>
            <form
              className="space-y-3 border-t border-border pt-4"
              onSubmit={(event) => {
                event.preventDefault()
                if (!confirmed) return
                const parsed = reportingOperationalBindingSchema.safeParse({
                  key: bindingKey,
                  productKey: bindingProduct,
                  kind,
                  externalId,
                  environment,
                  includedFrom: bindingStart,
                  intervalSeconds: isCheck && interval ? Number(interval) : null,
                })
                if (!parsed.success) {
                  setMessage(formatReportingValidationIssues(parsed.error.issues))
                  return
                }
                bindingMutation.mutate({ companyId, binding: parsed.data })
              }}
            >
              <h3 className="font-medium">3. Operational ownership</h3>
              <p className="text-xs text-muted-foreground">
                Use exact monitor registration UUIDs or verified incident/deployment project keys. This grants reporting
                access to the mapped records; never infer ownership from display names. Incident environment stays
                unknown because its source has no authoritative environment column.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Product">
                  <Select
                    value={bindingProduct || undefined}
                    onValueChange={(value) => {
                      setBindingProduct(value)
                      setConfirmed(false)
                    }}
                  >
                    <SelectTrigger aria-label="Operational product">
                      <SelectValue placeholder="Choose product" />
                    </SelectTrigger>
                    <SelectContent>{productOptions}</SelectContent>
                  </Select>
                </Field>
                <Field label="Binding key">
                  <Input
                    value={bindingKey}
                    onChange={(event) => {
                      setBindingKey(event.target.value)
                      setConfirmed(false)
                    }}
                    required
                  />
                </Field>
                <Field label="Source kind">
                  <Select
                    value={kind}
                    onValueChange={(value) => {
                      const parsed = reportingOperationalBindingSchema.innerType().shape.kind.parse(value)
                      setKind(parsed)
                      setEnvironment(
                        parsed === "scenario" ? "prod" : parsed === "incident-project" ? "unknown" : "production",
                      )
                      setConfirmed(false)
                    }}
                  >
                    <SelectTrigger aria-label="Operational kind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["uptime", "scenario", "incident-project", "deploy-project"].map((value) => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Exact registration UUID or project key">
                  <Input
                    value={externalId}
                    onChange={(event) => {
                      setExternalId(event.target.value)
                      setConfirmed(false)
                    }}
                    required
                  />
                </Field>
                <Field label="Source environment">
                  <Select
                    value={environment}
                    onValueChange={(value) => {
                      setEnvironment(value)
                      setConfirmed(false)
                    }}
                  >
                    <SelectTrigger aria-label="Operational environment">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {environmentOptions.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Verified inclusion timestamp (ISO 8601 with timezone)">
                  <Input
                    value={bindingStart}
                    onChange={(event) => {
                      setBindingStart(event.target.value)
                      setConfirmed(false)
                    }}
                    placeholder="2026-09-01T00:00:00Z"
                    required
                  />
                </Field>
                {isCheck ? (
                  <Field label="Verified interval seconds (blank if unknown)">
                    <Input
                      type="number"
                      min="1"
                      max="604800"
                      value={interval}
                      onChange={(event) => {
                        setInterval(event.target.value)
                        setConfirmed(false)
                      }}
                    />
                  </Field>
                ) : null}
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
                <span>
                  I verified this source belongs to the selected company and product. Save this explicit ownership
                  mapping.
                </span>
              </label>
              <Button type="submit" loading={bindingMutation.isLoading} disabled={!confirmed || !bindingProduct}>
                Save operational binding
              </Button>
            </form>
            <SeoInventorySetup companyId={companyId} products={products} onSaved={onSaved} />
            {message ? (
              <p role="status" className="rounded border border-border p-3 text-sm">
                {message}
              </p>
            ) : null}
          </div>
        </details>
      </CardContent>
    </Card>
  )
}
